import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import invariant from "tiny-invariant";

import { isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import {
    AnalyticsContext,
    AutoPayFailedOrderActionPayload,
    CHECKOUT_WEB_XP,
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
    CheckoutWebDynamicConfigContext,
    ConfirmOrderActionPayload,
    useCustomHardwareBack,
} from "@swiggy-private/checkout-core";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

// constants
import { PLACE_ORDER_EXTRA_PARAMS } from "@payment-client/constants/Order";

// hooks
import { useOnPay } from "@payment-client/hooks/UseOnPay";

// helpers
import { getCODPaymentMethodForAutoPay } from "@payment-client/helpers/CodHelper";
import { onOrderPlacedEvent } from "@payment-client/analytics/events/Payment";
import {
    autopayAutoplaceGotoTrackClickEvent,
    autopayAutoplacePlaceOrderPayOnNextScreenClickEvent,
    autopayAutoplaceGoBackClickEvent,
    autoPayEligiblePaymentFailureScreenEvent,
    autopaySubtleConsentCancelClickEvent,
} from "@payment-client/analytics/events/PaymentFailed";
import { postOrderEventHelper } from "@payment-client/helpers/PostOrderHelper";
import { getAutoPayVariantType } from "./components/helpers";
import { isInAppUpiMethodInOrderData } from "@payment-client/helpers/InAppUpiHelper";
import { handleAutopayFeedback } from "./utils";

// components
import { AutopayPaymentFailureScreenContent } from "./components/AutopayPaymentFailureScreenContent";
import { AutopaySubtleConsent } from "./components/autopay-subtle-consent";

// types
import { RouteProps } from "@payment-client/interfaces/Router";
import { USER_CONSENT_KEY } from "@payment-client/constants/OrderInterruptionAlert";
import { BOOLEAN_AS_STRING } from "@swiggy-private/payment-core";

function AutopayPaymentFailureScreen(_props: RouteProps): ReactElement | null {
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { clickEvent, impressionEvent, screenViewEvent } = useContext(AnalyticsContext);
    const { billTotal } = useContext(PaymentPayContext);
    const { checkoutWebXP } = useContext(CheckoutWebDynamicConfigContext);

    const isSubtleConsentAPiCallDone = useRef(false);

    const onPay = useOnPay();

    const isPlaceOrderWithCashSuccess = checkoutState.type === CheckoutActionTypes.AUTOPAY_ORDER_PROCESS_SUCCESSFUL;

    const CODPaymentMethod = useMemo(() => {
        return getCODPaymentMethodForAutoPay(paymentResponse?.paymentGroup);
    }, [paymentResponse]);

    invariant(!isEmpty(CODPaymentMethod), "Autopay: COD payment method missing");

    const autoPayFailureScreenPayload = (checkoutState.type == CheckoutActionTypes.AUTOPAY_ELIGIBLE_PAYMENT_FAILED
        ? checkoutState.payload
        : undefined) as AutoPayFailedOrderActionPayload;

    const isInAppUpiMethod = isInAppUpiMethodInOrderData(autoPayFailureScreenPayload?.failedOrder);
    const cashPlaceOrderSuccessPayload = (isPlaceOrderWithCashSuccess
        ? checkoutState.payload
        : undefined) as ConfirmOrderActionPayload;

    const { isConsentMode, isSubtleAutoPlace } = useMemo(
        () =>
            getAutoPayVariantType({
                payload: autoPayFailureScreenPayload,
                successPayload: cashPlaceOrderSuccessPayload,
            }),
        [autoPayFailureScreenPayload, cashPlaceOrderSuccessPayload],
    );

    const failureReason = autoPayFailureScreenPayload?.paymentFailureResponse?.failureReason ?? "";
    const autopayFeedbackXp = useMemo(
        () => checkoutWebXP[CHECKOUT_WEB_XP.AUTOPAY_ABANDONMENT_FEEDBACK] === BOOLEAN_AS_STRING.TRUE,
        [checkoutWebXP],
    );

    const payAsCash = useCallback(() => {
        if (CODPaymentMethod) {
            return onPay(
                CODPaymentMethod,
                {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    // When a autopay order is placed we dont want to show duplicate half card
                    // bcz autopay comes into picture after a transaction fails
                    // when user paid using example: card they would have gotten this half card already
                    // current flow: card pay -> duplicate half card -> force place duplicate order -> payment fails -> autopay screen -> pay as cash -> duplicate order(this one is not needed)
                    [USER_CONSENT_KEY]: "proceed_with_duplicate_order",
                    proceed_with_duplicate_order: true,
                    // override these values so that cash order is placed without swiggy money
                    [PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT]: 0,
                    pay_on_delivery: {
                        variant: (checkoutState.payload as AutoPayFailedOrderActionPayload)
                            ?.autoPayPaymentFailureVariant,
                        failed_order_id: (checkoutState.payload as AutoPayFailedOrderActionPayload)?.failedOrder
                            ?.orderId,
                    },
                    /* eslint-enable @typescript-eslint/naming-convention */
                },
                { isRewardApplicable: false },
            );
        }
    }, [CODPaymentMethod, checkoutState.payload, onPay]);

    const sendEventsOnOrderSuccess = useCallback(() => {
        if (checkoutState.type === CheckoutActionTypes.AUTOPAY_ORDER_PROCESS_SUCCESSFUL) {
            if (checkoutState.payload?.isOrderProcessed != true) return;
            onOrderPlacedEvent(impressionEvent);
        }
    }, [checkoutState, impressionEvent]);

    const handleGotoTrackCtaClick = useCallback(() => {
        autopayAutoplaceGotoTrackClickEvent(clickEvent, billTotal);

        // To take user to track screen we use postOrder NB method,
        // it will close the webview and take user to track screen
        if (
            checkoutState.type === CheckoutActionTypes.AUTOPAY_ORDER_PROCESS_SUCCESSFUL &&
            checkoutState.payload?.order?.rawResponse?.order_data
        ) {
            postOrderEventHelper(checkoutState.payload.order.rawResponse.order_data, true);
        }
    }, [billTotal, checkoutState.payload, checkoutState.type, clickEvent]);

    const handlePrimaryCtaClick = useCallback(() => {
        autopayAutoplacePlaceOrderPayOnNextScreenClickEvent(clickEvent, billTotal);
        payAsCash();
    }, [billTotal, clickEvent, payAsCash]);

    const orderId: string = useMemo(() => {
        if (checkoutState?.type === CheckoutActionTypes.AUTOPAY_ELIGIBLE_PAYMENT_FAILED) {
            return checkoutState?.payload?.failedOrder?.orderId?.toString() || "";
        }
        return "";
    }, [checkoutState]);

    const handleSecondaryCtaClick = useCallback(async () => {
        if (isSubtleAutoPlace) {
            autopaySubtleConsentCancelClickEvent(clickEvent, billTotal);
        } else {
            // autopay feedback is rendered if enabled
            if (autopayFeedbackXp) {
                await handleAutopayFeedback(orderId);
            }

            autopayAutoplaceGoBackClickEvent(clickEvent, billTotal);
        }

        setCheckoutState({
            type: CheckoutActionTypes.SHOW_PAYMENTS,
            payload: {
                reload: isInAppUpiMethod,
            },
        });
    }, [autopayFeedbackXp, billTotal, clickEvent, isInAppUpiMethod, isSubtleAutoPlace, orderId, setCheckoutState]);

    const onBack = useCallback(async () => {
        if (isSubtleConsentAPiCallDone.current) {
            return;
        }

        // autopay feedback is rendered if enabled
        if (autopayFeedbackXp) {
            await handleAutopayFeedback(orderId);
        }

        setCheckoutState({
            type: CheckoutActionTypes.SHOW_PAYMENTS,
            payload: {
                reload: isInAppUpiMethod,
            },
        });
    }, [autopayFeedbackXp, setCheckoutState, isInAppUpiMethod, orderId]);

    useEffect(() => {
        if (checkoutState.type === CheckoutActionTypes.AUTOPAY_ORDER_PROCESS_SUCCESSFUL) {
            sendEventsOnOrderSuccess();
        }
    }, [checkoutState, sendEventsOnOrderSuccess]);

    useEffectOnce(() => {
        if (
            checkoutState.type !== CheckoutActionTypes.AUTOPAY_ELIGIBLE_PAYMENT_FAILED ||
            !isEmpty(cashPlaceOrderSuccessPayload)
        ) {
            return;
        }

        autoPayEligiblePaymentFailureScreenEvent(screenViewEvent, billTotal, failureReason);
    });

    useCustomHardwareBack([CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED], onBack);

    if (
        checkoutState.type !== CheckoutActionTypes.AUTOPAY_ELIGIBLE_PAYMENT_FAILED &&
        checkoutState.type !== CheckoutActionTypes.AUTOPAY_ORDER_PROCESS_SUCCESSFUL
    ) {
        return null;
    }

    if (isSubtleAutoPlace) {
        return (
            <AutopaySubtleConsent
                onCancel={handleSecondaryCtaClick}
                payAsCash={payAsCash}
                successPayload={cashPlaceOrderSuccessPayload}
                goToTrackScreen={handleGotoTrackCtaClick}
                isSubtleConsentAPiCallDone={isSubtleConsentAPiCallDone}
            />
        );
    }

    if (isConsentMode) {
        return (
            <AutopayPaymentFailureScreenContent
                payload={autoPayFailureScreenPayload}
                codData={CODPaymentMethod}
                failureReason={failureReason}
                successPayload={cashPlaceOrderSuccessPayload}
                goToTrackScreen={handleGotoTrackCtaClick}
                handlePrimaryCtaClick={handlePrimaryCtaClick}
                handleSecondaryCtaClick={handleSecondaryCtaClick}
            />
        );
    }

    return null;
}

export default memo(AutopayPaymentFailureScreen);
